import { isPdfMode, pdfVariantParam } from "../utils/pdfBootParams"
import { ThemeContext } from "./ThemeContext"
import { PALETTE } from "./palette"
import { CssBaseline, ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material"
import React, { type ReactNode, useEffect, useMemo, useState } from "react"

// must stay in sync with the "@media screen and (min-width: 1140px)" rule
// below and with MOBILE_ZOOM's counterpart in PageLayout
const DESKTOP_SHEET_ZOOM = 1.3
const ZOOM_BREAKPOINT = 1140
// keeps a 1440 laptop (~1372px of client width after the scrollbar) clear of
// the dock threshold instead of falling into the hamburger
const SHEET_SIDEBAR_GAP = 40

interface ThemeProviderProps {
    children: ReactNode
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
    const mmToPx = (mm: number) => mm * 3.7795275591 // px at 96 DPI
    const sidebarWidth = 300
    const pageWidthMm = 210
    const pageHeightMm = 297
    const pagePadding = 40
    const pageWidth = mmToPx(pageWidthMm)
    const pageHeight = mmToPx(pageHeightMm)

    const [themeMode, setThemeMode] = useState<"light" | "system" | "dark">(() => {
        const fromPdf = pdfVariantParam("theme", ["light", "dark"])
        if (fromPdf) return fromPdf
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("themeMode")
            if (saved && ["light", "system", "dark"].includes(saved)) {
                return saved as "light" | "system" | "dark"
            }
        }
        return "light"
    })

    const [audienceMode, setAudienceMode] = useState<"thai" | "international">(() => {
        const fromPdf = pdfVariantParam("audience", ["thai", "international"])
        if (fromPdf) return fromPdf
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("audienceMode")
            if (saved && ["thai", "international"].includes(saved)) {
                return saved as "thai" | "international"
            }
        }
        return "international" // until geolocation loads
    })

    useEffect(() => {
        // pdf mode pins the variant explicitly — geolocation must not
        // override it mid-print
        if (isPdfMode) return
        const detectLocation = async () => {
            if (typeof window !== "undefined" && !localStorage.getItem("audienceMode")) {
                try {
                    const response = await fetch("https://ipapi.co/country_code/")
                    const countryCode = await response.text()
                    const isThailand = countryCode.trim() === "TH"
                    setAudienceMode(isThailand ? "thai" : "international")
                } catch {
                    setAudienceMode("international")
                }
            }
        }
        detectLocation()
    }, [])

    const [skillsStyle, setSkillsStyle] = useState<"simple" | "highlighted">(() => {
        const fromPdf = pdfVariantParam("skills", ["simple", "highlighted"])
        if (fromPdf) return fromPdf
        if (typeof window !== "undefined") {
            const saved = localStorage.getItem("skillsStyle")
            if (saved && ["simple", "highlighted"].includes(saved)) {
                return saved as "simple" | "highlighted"
            }
        }
        return "highlighted"
    })

    const visibilityTier: 1 | 2 | 3 = 1

    // phone keystroke state lives in PhoneProvider's own context — typing
    // must not re-render the whole resume tree

    const [systemPrefersDark] = useState(() => {
        if (typeof window !== "undefined") {
            return window.matchMedia("(prefers-color-scheme: dark)").matches
        }
        return false
    })

    // build-time PDF generator signal: with ?pdf=1 the page is printed by
    // headless Chrome with print media emulated, so the same CSS path as
    // window.print applies — pdf mode only pins the variant and forces the
    // full-page sheet regardless of the 794px viewport
    const isPdfModeState = isPdfMode

    useEffect(() => {
        if (!isPdfModeState) return
        document.documentElement.setAttribute("data-pdf-ready", "true")
    }, [isPdfModeState])

    useEffect(() => {
        localStorage.setItem("themeMode", themeMode)
    }, [themeMode])

    useEffect(() => {
        localStorage.setItem("audienceMode", audienceMode)
    }, [audienceMode])

    useEffect(() => {
        localStorage.setItem("skillsStyle", skillsStyle)
    }, [skillsStyle])

    const isDarkMode = themeMode === "dark" || (themeMode === "system" && systemPrefersDark)

    const colors = PALETTE[isDarkMode ? "dark" : "light"]

    const theme = createTheme({
        palette: {
            mode: isDarkMode ? "dark" : "light",
            primary: {
                main: colors.accent,
                dark: colors.accentHover,
            },
            // pinned to accent so a stray color="secondary" can never leak
            // off-brand — not a palette role; no component should use it
            secondary: {
                main: colors.accent,
            },
            text: {
                primary: colors.primaryText,
                // solid, never alpha — alpha(.8) drops contrast below AAA
                secondary: colors.secondaryText,
            },
            // résumé inversion (see palette.ts): page field = surface hex,
            // paper sheets = background hex
            background: {
                default: colors.surface,
                paper: colors.background,
            },
            divider: colors.border,
        },
        typography: {
            fontFamily: '"General Sans", system-ui, sans-serif',
            fontSize: 11.5,
            h1: { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' },
            h2: { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' },
            h3: { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' },
            h4: { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' },
            h5: { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' },
            h6: { fontFamily: '"Fraunces", Georgia, "Times New Roman", serif' },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    // background-only selection wash (paired cascade: unset
                    // color keeps each text's own color). Chrome's default is
                    // opaque blue under our mid-gray body text — reads as
                    // broken contrast; Safari's translucent wash is the look
                    // we want, stated explicitly so every browser matches.
                    // Alpha, not solid: Safari forces a wash on author colors
                    // anyway, so tints behave predictably cross-browser
                    "::selection": {
                        backgroundColor: isDarkMode
                            ? "rgba(250, 250, 250, 0.20)" // paper ink at 20%
                            : "rgba(24, 24, 27, 0.10)", // ink at 10%
                    },
                    ".visibility-tier-2": {
                        display: visibilityTier < 2 ? "none" : "inline",
                    },
                    ".visibility-tier-3": {
                        display: visibilityTier < 3 ? "none" : "inline",
                    },
                    // screen-only display magnification of the A4 sheet —
                    // print resets it below. Mobile mode keeps the sheet at
                    // 100% width (PageLayout), so no zoom there. 1140 ≈ the
                    // zoomed sheet (794×1.3) plus margins
                    "@media screen and (min-width: 1140px)": {
                        "div.page": { zoom: 1.3 },
                    },
                    "@media print": {
                        "@page": {
                            size: "A4",
                            margin: 0,
                        },
                        "html, body, .content, main.MuiBox-root": {
                            margin: 0,
                            padding: 0,
                        },
                        ".sidebar-container": {
                            display: "none",
                        },
                        "div.page": {
                            margin: 0,
                            padding: pagePadding,
                            width: "100%",
                            height: pageHeight,
                            // cancel the screen-only zooms so the PDF keeps
                            // original size
                            zoom: 1,
                            border: 0,
                            boxShadow: "none",
                            pageBreakAfter: "always",
                            overflow: "hidden",
                        },
                        // bare selector (semantic chrome carries the class
                        // too) and !important — emotion may emit an element's
                        // own sx display AFTER this rule and later insertion
                        // would win
                        ".no-print": {
                            display: "none !important",
                        },
                        "div.vercel-live-feedback-full-frame, vercel-live-feedback": {
                            display: "none",
                        },
                    },
                },
            },
            MuiTypography: {
                styleOverrides: {
                    body1: ({ theme }) => ({
                        color: theme.palette.text.secondary,
                        marginBottom: 8 * verticalSpacing * 0.7,
                        textAlign: "justify",
                        // no textJustify: "inter-character" — a CJK technique
                        // that pries Latin words apart
                        hyphens: "auto",
                        fontSize: "0.90rem",
                        // below the 1140px breakpoint the sheet is too narrow
                        // to justify (needs ~45+ chars per line) — ragged
                        // right there; nested so it wins the cascade
                        "@media screen and (max-width: 1139px)": {
                            textAlign: "left",
                        },
                    }),
                    body2: ({ theme }) => ({
                        color: theme.palette.text.secondary,
                    }),
                    caption: ({ theme }) => ({
                        color: theme.palette.text.secondary,
                    }),
                    overline: ({ theme }) => ({
                        color: theme.palette.text.secondary,
                    }),
                },
            },
        },
    })

    // measure on the first render, not default-then-correct: a wrong default
    // paints the full-width A4 sheet, the phone browser expands the layout
    // viewport to fit and fires no resize, stranding desktop mode
    const measureBreakpoints = () => {
        if (typeof window === "undefined") {
            return { small: false, medium: false, large: true }
        }
        const w = document.documentElement.clientWidth
        const zoom = w >= ZOOM_BREAKPOINT ? DESKTOP_SHEET_ZOOM : 1
        const small = zoom === 1
        const medium = w < pageWidth * zoom + SHEET_SIDEBAR_GAP + sidebarWidth
        return { small, medium, large: !small && !medium }
    }

    const [isSmallScreen, setIsSmallScreen] = useState(() => measureBreakpoints().small)
    const [isMediumScreen, setIsMediumScreen] = useState(() => measureBreakpoints().medium)
    const [isLargeScreen, setIsLargeScreen] = useState(() => measureBreakpoints().large)

    useEffect(() => {
        // thresholds scale with the zoom or the sidebar overlaps the paper
        // hysteresis: re-docking can bring the scrollbar back, re-triggering
        // collapse — only re-dock once the width clears the threshold by
        // this band
        const HYSTERESIS = 24
        const wasMediumRef = { current: false }
        const checkWidth = () => {
            // clientWidth, not innerWidth — excludes the scrollbar, so
            // measuring doesn't shift the layout it measures
            const w = document.documentElement.clientWidth
            const zoom = w >= ZOOM_BREAKPOINT ? DESKTOP_SHEET_ZOOM : 1
            const smallScreen = zoom === 1
            const totalRequiredWidth = pageWidth * zoom + SHEET_SIDEBAR_GAP + sidebarWidth
            const mediumScreen = wasMediumRef.current
                ? w < totalRequiredWidth + HYSTERESIS
                : w < totalRequiredWidth
            wasMediumRef.current = mediumScreen
            const largeScreen = !smallScreen && !mediumScreen

            setIsSmallScreen(smallScreen)
            setIsMediumScreen(mediumScreen)
            setIsLargeScreen(largeScreen)
        }

        checkWidth()
        // drop the boot-class overrides (index.html) once the first real
        // measurement has run
        document.documentElement.classList.remove("boot")
        window.addEventListener("resize", checkWidth)
        return () => window.removeEventListener("resize", checkWidth)
    }, [pageWidth, sidebarWidth])

    const verticalSpacing = 0.95
    const surfaceColor = colors.background
    const backgroundColor = colors.surface
    const borderColor = colors.border
    const faintColor = colors.faint
    // solid secondary — chips are reading content; the alpha tint failed
    // contrast
    const skillsTextColor = colors.secondaryText

    const contextValue = useMemo(
        () => ({
            theme,
            colors,
            themeSettings: {
                mode: themeMode,
                isDarkMode,
                setThemeMode,
                surfaceColor,
                backgroundColor,
                borderColor,
                faintColor,
            },
            layout: {
                sidebarWidth,
                pageWidth,
                pageHeight,
                pagePadding,
                verticalSpacing,
            },
            breakpoints: {
                isSmallScreen,
                isMediumScreen,
                isLargeScreen,
                isPdfMode: isPdfModeState,
                isFullPage: !isSmallScreen || isPdfModeState,
            },
            audience: {
                mode: audienceMode,
                setAudienceMode,
            },
            skills: {
                style: skillsStyle,
                setSkillsStyle,
                textColor: skillsTextColor,
            },
            visibility: {
                visibilityTier,
            },
        }),
        [
            theme,
            colors,
            themeMode,
            isDarkMode,
            setThemeMode,
            surfaceColor,
            backgroundColor,
            borderColor,
            faintColor,
            sidebarWidth,
            pageWidth,
            pageHeight,
            verticalSpacing,
            isSmallScreen,
            isMediumScreen,
            isLargeScreen,
            audienceMode,
            setAudienceMode,
            skillsStyle,
            setSkillsStyle,
            skillsTextColor,
            visibilityTier,
            isPdfModeState,
        ]
    )

    return (
        <ThemeContext.Provider value={contextValue}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    )
}
